import React from 'react';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { observer } from 'mobx-react-lite';
import { ApplicationSdk } from '../../../core/sdk';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Tooltip } from 'primereact/tooltip';
import { Dropdown } from 'primereact/dropdown';
import { FileUpload } from 'primereact/fileupload';

const KnowledgeEditorFooter = observer(({ onSave, store }: any) => {
    const isAnswerTooLong = store.editor?.isAnswerTooLong || false;
    const isQuestionTooShort = store.editor?.isQuestionTooShort || false;
    const isFormInvalid = isAnswerTooLong || isQuestionTooShort;

    return (
        <div className="flex">
            <div>
                <Button
                    label="Speichern + Schließen"
                    icon="pi pi-save"
                    onClick={onSave}
                    disabled={isFormInvalid}
                    autoFocus
                />
                {isQuestionTooShort && (
                    <div
                        style={{
                            fontSize: '0.8rem',
                            color: '#e74c3c',
                            marginTop: '0.25rem',
                        }}
                    >
                        Die Frage ist zu kurz (min. 3 Zeichen)
                    </div>
                )}
                {isAnswerTooLong && (
                    <div
                        style={{
                            fontSize: '0.8rem',
                            color: '#e74c3c',
                            marginTop: '0.25rem',
                        }}
                    >
                        Die Antwort ist zu lang (max. 600 Zeichen)
                    </div>
                )}
            </div>
        </div>
    );
});

const KnowledgeModuleEditor = observer(({ store }: { store: any }) => {
    const handleClose = () => {
        store.closeEditor();
    };

    return (
        <Dialog
            visible={!!store.editor}
            header="Wissensmodul-Eintrag bearbeiten"
            footer={() => (
                <KnowledgeEditorFooter
                    onSave={async () => {
                        await store.onSaveEntry();
                    }}
                    store={store}
                />
            )}
            onHide={handleClose}
            style={{ width: '40rem' }}
        >
            {store.editor && (
                <div>
                    <div style={{ marginBottom: '1rem' }}>
                        <label
                            htmlFor="entryTitle"
                            style={{ display: 'block', marginBottom: '0.5rem' }}
                        >
                            Frage
                        </label>
                        <InputText
                            id="entryTitle"
                            value={store.editor.question || ''}
                            onChange={e => {
                                store.editor.question = e.target.value;
                            }}
                            placeholder="Frage eingeben"
                            style={{
                                width: '100%',
                                borderColor: store.editor.isQuestionTooShort
                                    ? '#e74c3c'
                                    : undefined,
                                backgroundColor: store.editor.isQuestionTooShort
                                    ? '#fdf2f2'
                                    : undefined,
                            }}
                            data-pr-tooltip={
                                store.editor.isQuestionTooShort
                                    ? 'Die Frage ist zu kurz! Mindestens 3 Zeichen erforderlich.'
                                    : 'Geben Sie hier die Frage ein (min. 3 Zeichen, max. 80 Zeichen)'
                            }
                            data-pr-position="top"
                            data-pr-disabled={false}
                            invalid={store.editor.isQuestionTooShort}
                        />
                        <div
                            style={{
                                fontSize: '0.8rem',
                                color: store.editor.isQuestionTooShort ? '#e74c3c' : '#666',
                                marginTop: '0.25rem',
                                textAlign: 'right',
                            }}
                        >
                            {store.editor.questionCharacterInfo} Zeichen
                        </div>
                        <Tooltip target="#entryTitle" />
                    </div>
                    <div style={{ marginBottom: '1rem' }}>
                        <label
                            htmlFor="entryDescription"
                            style={{ display: 'block', marginBottom: '0.5rem' }}
                        >
                            Antwort
                        </label>
                        <InputTextarea
                            id="entryDescription"
                            value={store.editor.answer || ''}
                            onChange={e => {
                                store.editor.answer = e.target.value;
                            }}
                            placeholder="Antwort eingeben"
                            rows={5}
                            cols={30}
                            style={{
                                width: '100%',
                                borderColor: store.editor.isAnswerTooLong ? '#e74c3c' : undefined,
                                backgroundColor: store.editor.isAnswerTooLong
                                    ? '#fdf2f2'
                                    : undefined,
                            }}
                            invalid={store.editor.isAnswerTooLong}
                            data-pr-tooltip={
                                store.editor.isAnswerTooLong
                                    ? 'Die Antwort ist zu lang! Maximal 600 Zeichen erlaubt.'
                                    : 'Geben Sie hier die Antwort ein (max. 600 Zeichen)'
                            }
                            data-pr-position="top"
                        />
                        <div
                            style={{
                                fontSize: '0.8rem',
                                color: store.editor.isAnswerTooLong ? '#e74c3c' : '#666',
                                marginTop: '0.25rem',
                                textAlign: 'right',
                            }}
                        >
                            {store.editor.answerCharacterInfo} Zeichen
                        </div>
                        <Tooltip target="#entryDescription" />
                    </div>
                    <div style={{ marginBottom: '1rem' }}>
                        <label
                            htmlFor="entryContextUrl"
                            style={{ display: 'block', marginBottom: '0.5rem' }}
                        >
                            Kontext-Link
                        </label>
                        <InputText
                            id="entryContextUrl"
                            value={store.editor.context_url || ''}
                            onChange={e => {
                                store.editor.context_url = e.target.value;
                            }}
                            placeholder="Kontext-Url eingeben"
                            style={{ width: '100%' }}
                            data-pr-position="top"
                            data-pr-disabled={false}
                            invalid={false}
                        />
                        <Tooltip target="#entryContextUrl" />
                    </div>
                </div>
            )}
        </Dialog>
    );
});
const KnowledgeModuleEntry = observer(
    ({ id, entry, isActive, onClick, onEditModule, onDeleteModule }: any) => {
        const style = {
            marginBottom: '20px',
            border: '1px solid #ccc',
            borderRadius: '8px',
            padding: '15px',
            backgroundColor: isActive ? '#f9f9f9' : '#fff',
            cursor: 'grab',
            display: 'flex',
            flexDirection: 'column' as 'column',
            gap: '15px',
        };

        return (
            <div style={style}>
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Button
                            icon={isActive ? 'pi pi-chevron-down' : 'pi pi-chevron-right'}
                            className="p-button-text p-button-sm"
                            onClick={e => {
                                e.stopPropagation();
                                onClick();
                            }}
                        />
                        <div style={{ flex: 1 }}>Frage: {entry.question}</div>
                    </div>
                </div>
                {isActive && (
                    <div style={{ textAlign: 'left' }}>
                        <InputTextarea
                            id="knowledgeAnswer"
                            placeholder="Antwort"
                            value={entry.answer}
                            disabled={true}
                            rows={5}
                            cols={30}
                            style={{ width: '100%', marginBottom: '0.5rem' }}
                        />
                        <InputText
                            id="videoUrl"
                            placeholder="Context URL"
                            value={entry.context_url}
                            disabled={true}
                            style={{ flex: 1, width: '100%', marginBottom: '0.5rem' }}
                        />
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                            }}
                        >
                            <Button
                                icon="pi pi-pencil"
                                label="Bearbeiten"
                                className="p-button-sm p-button-secondary"
                                onMouseDown={e => e.stopPropagation()}
                                onPointerDown={e => e.stopPropagation()}
                                onClick={(e: any) => {
                                    e.stopPropagation();
                                    if (onEditModule) {
                                        onEditModule();
                                    }
                                }}
                            />
                            <Button
                                icon="pi pi-trash"
                                label="Löschen"
                                className="p-button-sm p-button-danger"
                                onMouseDown={e => e.stopPropagation()}
                                onPointerDown={e => e.stopPropagation()}
                                onClick={(e: any) => {
                                    e.stopPropagation();
                                    if (onDeleteModule) {
                                        onDeleteModule();
                                    }
                                }}
                            />
                        </div>
                    </div>
                )}
            </div>
        );
    }
);

const KnowledgeModuleContent = observer(({ store, sdk }: { store: any; sdk: any }) => {
    return (
        <div className="p-4">
            {(store.allEntries || []).map((entry: any) => (
                <KnowledgeModuleEntry
                    key={entry.id}
                    id={entry.id}
                    entry={entry}
                    isActive={store.menuActiveEntry === entry.id}
                    onClick={() => (store.menuActiveEntry = entry.id)}
                    onEditModule={() => store.onEditEntry(entry)}
                    onDeleteModule={async () => {
                        const response = await sdk.getDialogDelete({
                            title: 'Möchten Sie die Frage wirklich löschen?',
                            message: 'Bitte geben Sie "löschen" ein, um zu bestätigen.',
                            searchWord: 'löschen',
                            onConfirm: () => store.deleteEntry(entry.id),
                        });

                        if (response.confirmed) {
                            store.fetchKnowledge();
                            sdk.showSuccessToast('Frage erfolgreich gelöscht');
                        } else if (response.error) {
                            sdk.showErrorToast(response.error || 'Fehler beim Löschen der Frage');
                        }
                    }}
                />
            ))}
            <KnowledgeModuleEditor store={store} />
        </div>
    );
});

const KnowledgeTitle = observer(({ store }: { store: any }) => {
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.value.length <= 80) {
            store.knowledgeName = e.target.value;
        }
    };

    return (
        <div style={{ marginBottom: '1rem', width: '100%' }}>
            <label
                htmlFor="knowledgeName"
                style={{ display: 'block', marginBottom: '0.5rem' }}
            >
                Name
            </label>
            <InputText
                id="knowledgeName"
                placeholder="Titel"
                value={store.knowledgeName}
                onChange={handleInputChange}
                style={{ width: '100%' }}
                data-pr-tooltip="Der Titel muss mindestens 3 Zeichen lang sein."
                data-pr-position="top"
            />
            <Tooltip target="#knowledgeName" />
        </div>
    );
});

const KnowledgeDescription = observer(({ store }: { store: any }) => {
    const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        store.knowledgeDescription = e.target.value;
    };

    return (
        <div style={{ marginBottom: '1rem', width: '100%' }}>
            <label
                htmlFor="knowledgeDescription"
                style={{ display: 'block', marginBottom: '0.5rem' }}
            >
                Beschreibung
            </label>
            <InputTextarea
                id="knowledgeDescription"
                placeholder="Beschreibung"
                value={store.knowledgeDescription}
                onChange={handleInputChange}
                rows={5}
                cols={30}
                style={{ width: '100%' }}
            />
        </div>
    );
});

const KnowledgeTypeSelection = observer(({ store }: { store: any }) => {
    const typeOptions = [
        { label: 'Wissensmodul', value: 'module' },
        { label: 'PDF Datei', value: 'pdf' },
    ];

    return (
        <div style={{ marginBottom: '1rem', width: '100%' }}>
            <label
                htmlFor="knowledgeType"
                style={{ display: 'block', marginBottom: '0.5rem' }}
            >
                Typ
            </label>
            <Dropdown
                id="knowledgeType"
                value={store.knowledge_type}
                options={typeOptions}
                onChange={e => (store.knowledge_type = e.value)}
                placeholder="Typ auswählen"
                style={{ width: '100%' }}
            />
        </div>
    );
});

const KnowledgePdfUpload = observer(({ store }: { store: any }) => {
    const onSelect = (e: any) => {
        const file = e.files[0];
        if (file && file.type === 'application/pdf') {
            store.selectedPdfFile = file;
        }
    };

    const onRemove = () => {
        store.selectedPdfFile = null;
    };

    const onClear = () => {
        store.selectedPdfFile = null;
    };

    return (
        <div style={{ marginBottom: '1rem', width: '100%' }}>
            <label
                htmlFor="pdfUpload"
                style={{ display: 'block', marginBottom: '0.5rem' }}
            >
                PDF Datei
            </label>
            <FileUpload
                id="pdfUpload"
                mode="basic"
                accept="application/pdf"
                maxFileSize={10000000} // 10MB
                onSelect={onSelect}
                onRemove={onRemove}
                onClear={onClear}
                chooseLabel="PDF auswählen"
                uploadLabel="Hochladen"
                cancelLabel="Abbrechen"
                auto={false}
                customUpload={true}
                style={{ width: '100%' }}
            />
            {store.selectedPdfFile && (
                <div style={{ marginTop: '0.5rem', fontSize: '0.9rem', color: '#666' }}>
                    Ausgewählte Datei: {store.selectedPdfFile.name} (
                    {Math.round(store.selectedPdfFile.size / 1024)} KB)
                </div>
            )}
        </div>
    );
});

const KnowledgeDialog = observer(({ sdk }: { sdk: ApplicationSdk }) => {
    const store = sdk.currentDialog;

    const handleKnowledgeSave = async () => {
        try {
            await store.saveKnowledge();

            sdk.closeDialog({
                event: 'SAVED',
            });
            sdk.showSuccessToast('Wissen gespeichert');
        } catch (error: any) {
            sdk.showErrorToast(error?.message || 'Unbekannter Fehler');
        }
    };

    const handleKnowledgeLearn = async () => {
        try {
            await store.onLearnModule();
            sdk.closeDialog({
                event: 'SAVED',
            });
            sdk.showSuccessToast('Trainingsprozess durchgeführt');
        } catch (error: any) {
            sdk.showErrorToast(error?.message || 'Unbekannter Fehler');
        }
    };

    const isSaveable =
        !store.isLoading &&
        (store.knowledge_type !== 'pdf' || store.selectedPdfFile || !store.isNewKnowledge);

    const getDialogHeader = () => {
        if (store.isNewKnowledge) {
            return store.knowledge_type === 'pdf'
                ? 'Neues PDF-Wissen erstellen'
                : 'Neues Wissensmodul erstellen';
        }
        return store.knowledge_type === 'pdf' ? 'PDF-Wissen bearbeiten' : 'Wissensmodul bearbeiten';
    };

    return (
        <Dialog
            visible={!!sdk.currentDialog}
            modal
            header={getDialogHeader()}
            footer={() => (
                <div className="flex justify-content-between align-items-center">
                    <div className="flex align-items-left gap-2">
                        {!store.isNewKnowledge && store.knowledge_type === 'module' && (
                            <>
                                <Button
                                    label="Eintrag hinzufügen"
                                    icon="pi pi-plus"
                                    disabled={store.isLoading}
                                    onClick={() => store.onAddEntry()}
                                />
                                <Button
                                    label="Anlernen"
                                    icon="pi pi-brain"
                                    onClick={handleKnowledgeLearn}
                                    disabled={store.isLoading}
                                />
                            </>
                        )}
                    </div>
                    <div className="flex gap-2">
                        {store.isNewKnowledge && (
                            <Button
                                label="Abbrechen"
                                icon="pi pi-times"
                                className="p-button-secondary"
                                onClick={() => sdk.closeDialog()}
                            />
                        )}
                        <Button
                            label={store.saveButton}
                            icon="pi pi-check"
                            className="p-button-primary"
                            disabled={!isSaveable}
                            onClick={handleKnowledgeSave}
                        />
                    </div>
                </div>
            )}
            style={{
                width: store.uiWide ? '75rem' : '40rem',
                minHeight: '60vh',
            }}
            onHide={() => sdk.closeDialog()}
        >
            {store.isLoading ? (
                <div
                    className="flex justify-content-center align-items-center"
                    style={{ height: '400px' }}
                >
                    <ProgressSpinner />
                </div>
            ) : (
                <div
                    className="dialog-content"
                    style={{
                        display: 'flex',
                        gap: '2rem',
                        minHeight: '400px',
                    }}
                >
                    <div
                        className="left-column"
                        style={{
                            flex: store.uiWide ? '0 0 300px' : '1',
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        <div className="card p-3">
                            {store.isNewKnowledge && <KnowledgeTypeSelection store={store} />}
                            <KnowledgeTitle store={store} />
                            {store.knowledge_type === 'module' && (
                                <KnowledgeDescription store={store} />
                            )}
                            {store.knowledge_type === 'pdf' && <KnowledgePdfUpload store={store} />}
                        </div>
                    </div>
                    {!store.isNewKnowledge && store.knowledge_type === 'module' && (
                        <div
                            className="right-column"
                            style={{
                                flex: '1',
                                display: 'flex',
                                flexDirection: 'column',
                            }}
                        >
                            <div
                                className="card p-3"
                                style={{ flex: '1' }}
                            >
                                <h5>Wissenseinträge</h5>
                                <KnowledgeModuleContent
                                    sdk={sdk}
                                    store={store}
                                />
                            </div>
                        </div>
                    )}
                </div>
            )}
        </Dialog>
    );
});

export default KnowledgeDialog;
